import React, { useState } from 'react';
import { downloadFullExport } from './chat/exportData';

type Variant = 'menu' | 'standalone';

interface ExportDataButtonProps {
  theme?: 'light' | 'dark';
  variant?: Variant;
}

/**
 * Downloads everything this account owns as a ZIP.
 * Available to every signed-in user regardless of subscription status --
 * losing access to the models must never mean losing access to your data.
 */
const ExportDataButton: React.FC<ExportDataButtonProps> = ({ theme = 'light', variant = 'menu' }) => {
  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleClick = async () => {
    if (status === 'working') return;
    setStatus('working');
    setMessage('');

    try {
      const result = await downloadFullExport();
      setStatus('done');
      const parts = [
        `${result.conversations} percakapan`,
        `${result.messages} pesan`,
        `${result.files} lampiran`,
      ];
      setMessage(
        result.missingFiles.length
          ? `Terunduh: ${parts.join(', ')}. ${result.missingFiles.length} lampiran tidak ada di browser ini, lihat BACA-DULU.md.`
          : `Terunduh: ${parts.join(', ')}.`
      );
    } catch (error: any) {
      setStatus('error');
      setMessage(error?.message || 'Gagal mengunduh data.');
    }
  };

  const label =
    status === 'working' ? 'Menyiapkan…' : status === 'done' ? 'Unduh lagi' : 'Download data saya';

  const isMenu = variant === 'menu';

  // The menu variant lives inside inline-styled markup and is handed an explicit
  // theme. The standalone variant sits on a Tailwind page that switches on the
  // OS colour scheme, so it must follow `dark:` rather than a prop.
  const buttonStyle: React.CSSProperties | undefined = isMenu
    ? {
        width: '100%',
        padding: '16px',
        background: 'none',
        border: 'none',
        textAlign: 'left',
        cursor: status === 'working' ? 'wait' : 'pointer',
        color: theme === 'light' ? '#111827' : '#E5E7EB',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }
    : undefined;

  const buttonClass = isMenu
    ? undefined
    : 'inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium border ' +
      'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 ' +
      'hover:bg-gray-100 dark:hover:bg-[#2D2D2D] transition-colors ' +
      'disabled:cursor-wait disabled:opacity-70';

  return (
    <div style={isMenu ? undefined : { display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <button
        onClick={handleClick}
        disabled={status === 'working'}
        style={buttonStyle}
        className={buttonClass}
        onMouseEnter={(e) => {
          if (isMenu) e.currentTarget.style.backgroundColor = theme === 'light' ? '#F5F5F5' : '#2D2D2D';
        }}
        onMouseLeave={(e) => {
          if (isMenu) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {label}
      </button>

      {message && (
        <p
          style={
            isMenu
              ? {
                  margin: '0 16px 12px',
                  fontSize: '12px',
                  lineHeight: 1.5,
                  color: status === 'error' ? '#DC2626' : theme === 'light' ? '#6B7280' : '#9CA3AF',
                }
              : undefined
          }
          className={
            isMenu
              ? undefined
              : `text-xs leading-relaxed ${
                  status === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
                }`
          }
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default ExportDataButton;
